import { Box } from 'src/design/atoms/Box';
import { Container } from 'src/design/atoms/Container';
import { FrozenPane } from 'src/design/atoms/FrozenPane';
import { Loader } from 'src/design/atoms/Loader';
import { Progress } from 'src/design/atoms/Progress';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { useFrozenPane } from 'src/design/molecules/useFrozenPane';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams, useSearchParams } from 'react-router-dom';
import { PostDto, PostFilterDto } from 'src/api/FiretrolApiClient';
import { usePostSearch } from 'src/api/queries/posts/usePostSearch';
import { useJobs } from 'src/hooks/useJobs';
import { EditPost } from './components/EditPost';
import { Filters } from './components/Filters';
import { PostCard } from './components/PostCard';
import { PostItem } from './components/PostItem';
import { PostViewDialog } from './components/PostViewDialog';

export const ActivityFeedView: React.FC = () => {
  const { selectedJob } = useJobs();
  const composerPane = useFrozenPane();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const postId = searchParams.get('postId');
  const [sort, setSort] = React.useState('updated_at');
  const [filter, setFilter] = React.useState('all');
  const [postType, setPostType] = React.useState('all');
  const [query, setQuery] = React.useState('');
  const [openPost, setOpenPost] = React.useState<PostDto | null>(null);
  const [filters, setFilters] = React.useState<PostFilterDto>({
    visibility: 'all',
    resolved: 'unresolved',
    assignedToMe: false,
    createdByMe: false,
    constraint: false,
    critical: false,
    cost: false,
    ctc: false,
    assigned: false,
  });

  const { data, isLoading, hasNextPage, fetchNextPage, refetch } =
    usePostSearch(selectedJob?.id, { filters, sort, postType, query });

  const posts = React.useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const onFilterChange = (key: string, value: string | boolean) => {
    setFilters((prevState) => ({ ...prevState, [key]: value }));
  };

  const closePostView = () =>
    setSearchParams(
      (prev) => {
        prev.delete('postId');
        return prev;
      },
      { replace: true },
    );

  const renderPosts = () => {
    if (posts.length === 0) {
      return <Text ta="center">No posts matching your filters</Text>;
    }
    return posts.map((post) => (
      <Box key={post.id} mb="md">
        <PostItem item={post} openEditDialog={setOpenPost} />
      </Box>
    ));
  };

  const openEditFromView = (item: PostDto) => {
    closePostView();
    setOpenPost(item);
  };

  return (
    <Container size="lg">
      <Stack gap="md">
        <PostViewDialog
          onClose={closePostView}
          jobId={Number(id)}
          postId={postId}
          open={!!postId}
          openEditDialog={openEditFromView}
        />
        <FrozenPane
          ref={composerPane.ref}
          frozen={composerPane.frozen}
          data-testid="activity-feed-frozen-header"
        >
          <PostCard onPostCreated={() => setOpenPost(null)} />
        </FrozenPane>
        <Filters
          filters={filters}
          sort={sort}
          onFiltersChange={onFilterChange}
          onSortChange={(value) => setSort(value)}
          postType={postType}
          onPostTypeChange={(value) => setPostType(value)}
          filter={filter}
          query={query}
          onFilterChange={(value) => setFilter(value)}
          onQueryChange={(value) => setQuery(value)}
        />
        <EditPost
          post={openPost}
          handleClose={() => setOpenPost(null)}
          onPostSaved={() => setOpenPost(null)}
        />
        {isLoading ? (
          <div className="text-center m-t-0">
            <Progress value={100} animated />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={posts.length}
            next={() => fetchNextPage()}
            hasMore={hasNextPage}
            loader={
              <div className="text-center m-t-15">
                <Loader />
              </div>
            }
            refreshFunction={() => refetch()}
            pullDownToRefresh={false}
            pullDownToRefreshThreshold={50}
            pullDownToRefreshContent={
              <Text ta="center">↓ Pull down to refresh</Text>
            }
            releaseToRefreshContent={
              <Text ta="center">↑ Release to refresh</Text>
            }
          >
            {renderPosts()}
          </InfiniteScroll>
        )}
      </Stack>
    </Container>
  );
};
